import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useBiz } from '../BizContext';
import { errMsg } from '../lib';

// Shrink big photos in the browser before upload (HEIC and GIF go through untouched — the server handles them).
async function shrinkImage(file) {
  if (!file.type.startsWith('image/') || file.type === 'image/gif' || /hei[cf]/i.test(file.type) || file.size < 600 * 1024) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 2000 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale); canvas.height = Math.round(bmp.height * scale);
    canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.86));
    return blob ? new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }) : file;
  } catch { return file; }
}

export function useEntries(kind, id) {
  const { api, actions } = useBiz();
  const parentType = kind === 'goal' ? 'goal' : 'initiative';
  const [entries, setEntries] = useState(null);
  const [progress, setProgress] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get('/business/entries', { params: { parent_type: parentType, parent_id: id } }); setEntries(r.data.entries); }
    catch (e) { toast.error(errMsg(e, 'Could not load the journal')); setEntries([]); }
  }, [api, parentType, id]);
  useEffect(() => { setEntries(null); load(); }, [load]);

  const replace = (doc) => setEntries((l) => (l || []).map((e) => (e.id === doc.id ? doc : e)));
  const wrap = async (fn, ok) => { try { const r = await fn(); if (ok) toast.success(ok); return r; } catch (e) { toast.error(errMsg(e, 'Something went wrong')); return null; } };

  return {
    entries, progress, reload: load,
    post: (body) => wrap(async () => { const r = await api.post('/business/entries', { parent_type: parentType, parent_id: id, ...body }); setEntries((l) => [r.data, ...(l || [])]); return r.data; }, body.type === 'idea' ? 'Idea kept — promote it when you’re ready' : 'Added to the journal'),
    upload: (files, meta) => wrap(async () => {
      const fd = new FormData();
      fd.append('parent_type', parentType); fd.append('parent_id', id); fd.append('text', meta.text || ''); fd.append('urgent', meta.urgent ? 'true' : 'false'); fd.append('needs', meta.needs || '');
      for (const f of files) fd.append('files', await shrinkImage(f), f.name);
      setProgress(0);
      try {
        const r = await api.post('/business/entries/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: (ev) => setProgress(ev.total ? Math.round((ev.loaded / ev.total) * 100) : null), timeout: 180000 });
        setEntries((l) => [r.data, ...(l || [])]);
        return r.data;
      } finally { setProgress(null); }
    }, files.length > 1 ? `${files.length} files added` : 'Added to the journal'),
    patch: (eid, body) => wrap(async () => { const r = await api.patch(`/business/entries/${eid}`, body); replace(r.data); return r.data; }),
    remove: (eid) => wrap(async () => { await api.delete(`/business/entries/${eid}`); setEntries((l) => l.filter((e) => e.id !== eid)); return true; }, 'Removed from the journal'),
    comment: (eid, text, mentions = []) => wrap(async () => { const r = await api.post(`/business/entries/${eid}/comments`, { text, mentions }); replace(r.data); return r.data; }),
    uncomment: (eid, cid) => wrap(async () => { const r = await api.delete(`/business/entries/${eid}/comments/${cid}`); replace(r.data); return r.data; }),
    promote: (eid, title) => wrap(async () => { const r = await api.post(`/business/entries/${eid}/promote`, { title }); replace(r.data.entry); await actions.reload(); await load(); return r.data.initiative; }, 'On the board — in Ideas, linked to this goal'),
  };
}
